import type { ChangeRequest, ChangeSubtask } from '../../types/changes';
import { SUBTASK_STATUS_LABELS } from './changeLabels';
import { canOfferInfraGateDispatch, canOpenTransportInfraGate } from './changeInfraIncidentAreas';
import { isChangeManager } from './changePermissions';
import type { User } from '../../types';

export type GateSubtaskDisplayStatus = 'concluida' | 'pendente' | 'em_atendimento';

export function gateSubtaskDisplayStatus(subtask: ChangeSubtask): GateSubtaskDisplayStatus {
  if (subtask.status === 'concluida') return 'concluida';
  if (
    subtask.incident?.outcome === 'sucesso' ||
    subtask.incident_outcome === 'sucesso' ||
    subtask.incident?.outcome === 'insucesso' ||
    subtask.incident_outcome === 'insucesso'
  ) {
    return 'concluida';
  }
  if (subtask.incident?.taken_by_name || subtask.status === 'em_atendimento') {
    return 'em_atendimento';
  }
  return 'pendente';
}

export function gateSubtaskStatusLabel(subtask: ChangeSubtask): string {
  if (subtask.invalidated_at) return 'Invalidada (histórico)';
  const kind = gateSubtaskDisplayStatus(subtask);
  if (kind === 'concluida') {
    if (
      subtask.incident?.outcome === 'insucesso' ||
      subtask.incident_outcome === 'insucesso'
    ) {
      return 'Encerrada sem sucesso';
    }
    return 'Concluída';
  }
  if (kind === 'em_atendimento') return 'Em atendimento';
  if (subtask.incident_dispatch_pending) return 'Pendente (após Transporte)';
  return 'Pendente';
}

export function gateSubtaskWaitHint(subtask: ChangeSubtask): string {
  if (gateSubtaskDisplayStatus(subtask) === 'concluida') {
    if (
      subtask.incident?.outcome === 'insucesso' ||
      subtask.incident_outcome === 'insucesso'
    ) {
      return 'Chamado cancelado ou encerrado sem sucesso no Incidente.';
    }
    return 'Chamado concluído no Incidente — gate de Implementar atendido.';
  }
  if (subtask.incident_dispatch_pending) {
    return 'O chamado será aberto no Incidente após GTEC e GSERV-I aprovarem o Transporte.';
  }
  if (subtask.incident?.taken_by_name) {
    return `Em atendimento por ${subtask.incident.taken_by_name} no Incidente.`;
  }
  if (subtask.ticket_id) {
    return 'Aguardando o grupo assumir e concluir o chamado no Incidente.';
  }
  return 'Aguardando abertura do chamado.';
}

export function gateSubtaskCardBorderClass(subtask: ChangeSubtask): string {
  if (subtask.invalidated_at) return 'mb-3 border-secondary opacity-75';
  const kind = gateSubtaskDisplayStatus(subtask);
  if (kind === 'concluida') {
    if (
      subtask.incident?.outcome === 'insucesso' ||
      subtask.incident_outcome === 'insucesso'
    ) {
      return 'mb-3 border-danger';
    }
    return 'mb-3 border-success';
  }
  if (kind === 'em_atendimento') return 'mb-3 border-primary';
  return 'mb-3';
}

export function canCancelGateSubtask(
  subtask: ChangeSubtask,
  change: ChangeRequest,
  user: { id?: number; role?: string; can_manage_changes?: boolean } | null | undefined,
): boolean {
  if (gateSubtaskDisplayStatus(subtask) === 'concluida') return false;
  if (!canOfferInfraGateDispatch(change) || !user?.id) return false;
  const mayOperateGate =
    isChangeManager(user as User) || canOpenTransportInfraGate(change, user);
  if (!mayOperateGate) return false;
  if (subtask.can_delete_subtask === false) return false;
  if (subtask.ticket_id) {
    return isChangeManager(user as User);
  }
  return true;
}

export function subtaskStatusLabelRaw(status: string): string {
  return SUBTASK_STATUS_LABELS[status] || status;
}
